import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, Plus, Mail, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { oapi, odownload } from '@/lib/officialsApi';
import { DownloadMenu, Loading, METHOD_LABEL, Money, PayStatusBadge, ReasonDialog, lineTitle, FlagIcon } from './PayBits';

const TOTALS = [['scheduled', 'Scheduled'], ['earned', 'Earned'], ['approved', 'Approved'], ['paid', 'Paid'], ['outstanding', 'Outstanding']];
const H = ({ children }) => <h4 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">{children}</h4>;

const LineItem = ({ l, currency }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 border-t first:border-t-0 text-sm" data-testid={`ol-line-${l.id}`}>
    <span className="w-20 text-xs text-slate-500">{l.date || '—'}</span>
    <div className="flex-1 min-w-[160px]">
      <div className="flex items-center gap-1.5 text-slate-800"><FlagIcon l={l} />{lineTitle(l)}</div>
      <div className="text-xs text-slate-500">{[l.role_label, l.game?.event_name].filter(Boolean).join(' · ')}</div>
    </div>
    <PayStatusBadge status={l.status} />
    <Money minor={l.amount_minor} currency={currency} className={`w-20 text-right font-semibold ${l.stage === 'void' ? 'line-through text-slate-400' : ''}`} />
  </li>
);

const PaymentItem = ({ p, currency, canReverse, onReverse }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 border-t first:border-t-0 text-sm" data-testid={`ol-payment-${p.id}`}>
    <span className="w-20 text-xs text-slate-500">{p.paid_on}</span>
    <div className="flex-1 min-w-[160px] text-slate-700">{METHOD_LABEL[p.method] || p.method}{p.reference ? ` · Ref ${p.reference}` : ''}
      <span className="text-xs text-slate-400"> · {p.games_covered} game{p.games_covered === 1 ? '' : 's'}</span>
      {p.status === 'reversed' && <div className="text-xs text-red-600">Reversed: {p.reverse_reason}</div>}
    </div>
    <Money minor={p.amount_minor} currency={currency} className={`w-20 text-right font-semibold ${p.status === 'reversed' ? 'line-through text-slate-400' : 'text-emerald-700'}`} />
    {canReverse && p.status === 'recorded' && (
      <button type="button" onClick={() => onReverse(p)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`ol-reverse-${p.id}`}><Undo2 className="w-3 h-3" />Reverse</button>
    )}
  </li>
);

export const OfficialLedgerDialog = ({ officialId, orgId, currency, perms, tick, onClose, recordPayment, addAdjustment, refresh }) => {
  const [d, setD] = useState(null);
  const [rev, setRev] = useState(null);
  const load = useCallback(() => {
    if (officialId) oapi(`/officials/payments/officials/${officialId}`, { orgId }).then(setD).catch(e => toast.error(e.message));
  }, [officialId, orgId]);
  useEffect(() => { setD(null); load(); }, [load, tick]);
  const name = d?.official.full_name || 'Official';
  const handoff = (fn, arg) => { onClose(); fn(arg); };
  const reverse = async ({ reason }) => {
    try {
      await oapi(`/officials/payments/payments/${rev.id}/reverse`, { method: 'POST', orgId, body: { reason } });
      toast.success('Payment reversed · balance restored');
      setRev(null);
      refresh();
    } catch (e) { toast.error(e.message); }
  };
  const download = (format) => odownload('/officials/payments/reports/statement', { org_id: orgId, official_id: officialId, format },
    `statement_${name.replace(/\s+/g, '_')}.${format}`).catch(e => toast.error(e.message));
  const email = () => oapi('/officials/payments/statements/send', { method: 'POST', orgId, body: { official_ids: [officialId] } })
    .then(r => (r.sent ? toast.success('Statement emailed') : toast('No email address on file for this official'))).catch(e => toast.error(e.message));
  return (
    <Dialog open={!!officialId} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="official-ledger-dialog">
        {!d ? <><DialogTitle className="sr-only">Loading ledger</DialogTitle><Loading /></> : (
          <>
            <DialogHeader>
              <DialogTitle className="text-[#00247D] text-xl" data-testid="ol-name">{name}</DialogTitle>
              <DialogDescription>Earnings ledger{d.official.email ? ` · ${d.official.email}` : ''}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap gap-2">
              {perms.has('record') && d.totals.outstanding > 0 && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handoff(recordPayment, { official_id: officialId, official_name: name })} data-testid="ol-pay"><Banknote className="w-4 h-4 mr-1.5" />Record payment</Button>
              )}
              {perms.has('approve') && <Button size="sm" variant="outline" onClick={() => handoff(addAdjustment, { official_id: officialId })} data-testid="ol-adjust"><Plus className="w-4 h-4 mr-1.5" />Adjustment</Button>}
              {perms.has('export') && <DownloadMenu onPick={download} label="Statement" testId="ol-statement" />}
              {perms.has('export') && <Button size="sm" variant="ghost" onClick={email} data-testid="ol-email-statement"><Mail className="w-4 h-4 mr-1.5" />Email statement</Button>}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {TOTALS.map(([k, l]) => (
                <div key={k} className="rounded-xl border px-3 py-2">
                  <div className="text-[10px] uppercase font-bold text-slate-500">{l}</div>
                  <Money minor={d.totals[k]} currency={currency} className={`font-black text-base sm:text-lg ${k === 'outstanding' ? 'text-red-600' : 'text-slate-900'}`} testId={`ol-total-${k}`} />
                </div>
              ))}
            </div>
            <div className="max-h-[45vh] overflow-y-auto space-y-5 pr-1">
              <section><H>Assignments & adjustments ({d.lines.length})</H>
                <ul>{[...d.lines].reverse().map(l => <LineItem key={l.id} l={l} currency={currency} />)}</ul>
                {!d.lines.length && <p className="text-sm text-slate-400 py-2">No assignments yet.</p>}
              </section>
              <section><H>Payments ({d.payments.length})</H>
                <ul>{d.payments.map(p => <PaymentItem key={p.id} p={p} currency={currency} canReverse={perms.has('record')} onReverse={setRev} />)}</ul>
                {!d.payments.length && <p className="text-sm text-slate-400 py-2">No payments recorded yet.</p>}
              </section>
            </div>
            <ReasonDialog open={!!rev} title="Reverse this payment?" danger confirmLabel="Reverse payment" testId="reverse-dialog"
              description="The payment stays in the history marked as reversed and the amount goes back to outstanding."
              onClose={() => setRev(null)} onSubmit={reverse} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
